/**
 * THE issue-type vocabulary. Every surface derives from this file.
 *
 * `tickets.type` is two things at once: the label a reporter picks, and the
 * key the verify router reads (packages/server/src/services/issueVerification.ts
 * pickVerifierType routes `accessibility` to axe and `design-token` to the
 * token re-runner). Before 2026-09-02 the list was hand-copied into 25+ files
 * across five surfaces, and every copy that drifted was a defect: the
 * extension had no label for `design-token` (rows rendered the raw slug), the
 * guest popovers offered 4 of 5 types, the AI prompt defined `design-bug` as
 * "visual mismatch", and any human could pick A11y and mis-route verify.
 *
 * Emily's calls (2026-09-01, product-psychology teardown):
 *   - the HUMAN picker is Bug / Mismatch / Improvement / General, each with a
 *     one-line description, in that order;
 *   - Design Debt, A11y and Design Token stay in the enum (the scanners stamp
 *     them, and they keep their verify routing) but leave every picker;
 *   - "Design Gap" is renamed Mismatch because it also covers Compare's copy
 *     findings; Compare stamps it (`design-gap`), no backfill;
 *   - `general` is the universal fallback on every create path. Bug is a
 *     default nowhere: the picker is the OVERRIDE, pre-filled by the
 *     auto-classifier (its own board row), never by a static Bug.
 *
 * Measured on prod before the change (external users, demo + machine paths
 * excluded, 2026-04-15..2026-09-01, n=373): 89.5% of human-picked types were
 * the untouched default `design-bug`; 12 of 60 people ever changed it.
 *
 * The guest client-review bundle is a concatenation of plain-JS sections and
 * cannot import this file; its copies (sections 0350/0380/0460) are pinned to
 * this one by packages/server/src/__tests__/live-review-popover-select-labels.test.ts.
 *
 * Copy rule: no em dashes in any string here (they are user-facing).
 */
export const ISSUE_TYPE_VALUES = [
    'design-bug',
    'design-gap',
    'improvement',
    'general',
    'design-debt',
    'accessibility',
    'design-token',
];
export const ISSUE_TYPES = [
    // No `description` field: the one-line hint the picker rendered under the
    // selected type (2026-09-02 to 2026-09-14) is gone on Emily's call ("drop
    // all of them"); the meanings live in the help corpus (knowledge.md).
    { value: 'design-bug', label: 'Bug', shortLabel: 'Bug', pickable: true },
    { value: 'design-gap', label: 'Mismatch', shortLabel: 'Mismatch', pickable: true },
    { value: 'improvement', label: 'Improvement', shortLabel: 'Improvement', pickable: true },
    { value: 'general', label: 'General', shortLabel: 'General', pickable: true },
    { value: 'design-debt', label: 'Design Debt', shortLabel: 'Debt', pickable: false },
    { value: 'accessibility', label: 'Accessibility', shortLabel: 'A11y', pickable: false },
    { value: 'design-token', label: 'Design Token', shortLabel: 'Token', pickable: false },
];
/** The four a human may choose, in picker order. */
export const PICKABLE_ISSUE_TYPES = ISSUE_TYPES.filter((t) => t.pickable);
/** The fallback on every create path when nobody (human or classifier) chose. */
export const DEFAULT_ISSUE_TYPE = 'general';
function toMap(pick) {
    return Object.fromEntries(ISSUE_TYPES.map((t) => [t.value, pick(t)]));
}
/** value -> full label ("Mismatch", "Design Token"). */
export const ISSUE_TYPE_LABELS = toMap((t) => t.label);
/** value -> badge label ("Mismatch", "Token"). */
export const ISSUE_TYPE_SHORT_LABELS = toMap((t) => t.shortLabel);
export function isIssueTypeValue(value) {
    return typeof value === 'string' && ISSUE_TYPE_VALUES.includes(value);
}
/**
 * Full label for a known value; an unknown value (extension custom types are
 * free text) renders as itself, capitalised, never as a crash.
 */
export function issueTypeLabel(value) {
    if (!value)
        return '';
    if (isIssueTypeValue(value))
        return ISSUE_TYPE_LABELS[value];
    return value.charAt(0).toUpperCase() + value.slice(1);
}
/** A fresh zero-filled per-type count, so no breakdown silently drops a type. */
export function emptyTypeBreakdown() {
    return Object.fromEntries(ISSUE_TYPE_VALUES.map((v) => [v, 0]));
}
//# sourceMappingURL=issueTypes.js.map